import React from "react";
import { Box, BreadcrumbInput, Menu, MenuPopup, MenuTrigger } from "@salestrak/ui";

import { EntityBreadcrumbLink, EntityVisual } from "#app/components/entity";
import { DROPDOWN_MENU_PARTS } from "#app/components/menus/menu-parts";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import {
  renderWorkflowMenuItems,
  useWorkflowMenuActions,
} from "#app/components/workflows/workflow-menu-items";
import { useAppData, type WorkspaceWorkflow } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";

/**
 * The workflow masthead crumb as a dropdown: this crumb IS the current page,
 * so a routing press has nowhere to go — instead the press opens the shared
 * workflow roster (`renderWorkflowMenuItems`), announced by the crumb's
 * trailing chevron. Only the ACTIVE crumb ever gets this; every other crumb
 * in a trail stays a plain routing link with no chevron. The menu offers
 * rename (inline, in the crumb's own slot) plus the roster's copy link,
 * create, appearance, stages, automations, settings, default, archive,
 * delete and sharing — rows the viewer lacks permission for stay visible but
 * disabled, with the reason on hover.
 */

export type WorkflowBreadcrumbMenuProps = {
  workflow: WorkspaceWorkflow;
  /** The masthead's Share-button gate, shared so the two affordances agree. */
  canShare: boolean;
  /** Opens the share dialog the masthead's Share button owns. */
  onShare: () => void;
};

export function WorkflowBreadcrumbMenu({
  workflow,
  canShare,
  onShare,
}: WorkflowBreadcrumbMenuProps) {
  const workflowId = workflow.workflowId;
  const { refetch: refetchAppData } = useAppData();
  const { push, replace } = useAppNavigate();
  const actions = useWorkflowMenuActions();

  const [renaming, setRenaming] = React.useState(false);
  const [createWorkflowOpen, setCreateWorkflowOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  // Controlled so Rename can begin after the menu finishes closing.
  const [menuOpen, setMenuOpen] = React.useState(false);
  // The rename starts AFTER the menu finishes closing, or the menu's focus
  // return steals the input's focus (see workflow-item.tsx).
  const pendingRenameRef = React.useRef(false);

  return (
    <>
      {renaming ? (
        // The crumb's own metrics and glyph, with the draft in the brand ink
        // — entering the edit moves nothing but the ring.
        <BreadcrumbInput
          aria-label="Workflow name"
          defaultValue={workflow.name ?? ""}
          onCancel={() => setRenaming(false)}
          onCommit={(next) => {
            setRenaming(false);
            void actions.rename(workflowId, next);
          }}
          visual={
            <EntityVisual
              entity={{ kind: "workflow", id: workflowId, workflow }}
              size="2xs"
              bareIconSize="md"
            />
          }
        />
      ) : (
        <Menu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          onOpenChangeComplete={(open) => {
            if (open || !pendingRenameRef.current) return;
            pendingRenameRef.current = false;
            setRenaming(true);
          }}
        >
          {/* Trigger wraps from OUTSIDE via render: the wrapper owns the
            * press, so the crumb itself stays a non-button (no interactive
            * nesting) — `dropdown` is what makes it read as the trigger's
            * face: hover wash held while the menu is open, pointer cursor,
            * trailing chevron. The wrapper is a Box, so the trigger is declared
            * non-native: Base UI otherwise expects a <button> here. */}
          <MenuTrigger
            nativeButton={false}
            render={<Box align="center" direction="row" minWidth={0} />}
          >
            <EntityBreadcrumbLink
              entity={{ kind: "workflow", id: workflowId, workflow }}
              active
              dropdown
            />
          </MenuTrigger>
          <MenuPopup align="start" side="bottom" width="action">
            {renderWorkflowMenuItems({
              parts: DROPDOWN_MENU_PARTS,
              workflow,
              actions,
              canShare,
              onShare,
              onRename: () => {
                pendingRenameRef.current = true;
              },
              onRequestDelete: () => setDeleteOpen(true),
              onRequestCreateWorkflow: () => setCreateWorkflowOpen(true),
              createIssueSource: "workflow_breadcrumb",
            })}
          </MenuPopup>
        </Menu>
      )}

      {actions.canCreateWorkflow ? (
        <CreateWorkflowDialog
          open={createWorkflowOpen}
          onOpenChange={setCreateWorkflowOpen}
          onCreated={async (createdId) => {
            await refetchAppData();
            // The new workflow's own page — only the settings list lands in
            // settings; every other entry point stays in the app.
            push(`/workflow/${createdId}`);
          }}
        />
      ) : null}

      <DeleteWorkflowDialog
        workflow={workflow}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        // The deleted workflow was the page — there is nothing to return to.
        onDeleted={() => replace("/")}
        onManageWorkflow={(id) => push(`/settings/workflows/${id}`)}
      />
    </>
  );
}
